import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  Animated,
} from 'react-native';
import { Text, useTheme, Portal, Modal } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NavigationProp } from '@react-navigation/native';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Eye,
  Send,
  FileText,
  DollarSign,
  Settings2,
  Link2,
  Image,
  X,
  Trash2,
  Coffee,
} from 'lucide-react-native';
import {
  useCreateEquipment,
  useUpdateEquipment,
} from '@coffeeplexity/api-client';
import { useDraftSaving } from '../hooks/useDraftSaving';
import { Toast } from '../utils/toast';
import { CrossPlatformAlert } from '../utils/crossPlatformAlert';
import { logger } from '../utils/logger';
import { EquipmentBasicInfo } from '../components/equipment/editor/EquipmentBasicInfo';
import { EquipmentPricing } from '../components/equipment/editor/EquipmentPricing';
import { EquipmentSpecifications } from '../components/equipment/editor/EquipmentSpecifications';
import { EquipmentAffiliateLinks } from '../components/equipment/editor/EquipmentAffiliateLinks';
import { EquipmentImages } from '../components/equipment/editor/EquipmentImages';
import { EquipmentPreview } from '../components/equipment/editor/EquipmentPreview';

// Helper function to format relative time
const getTimeAgo = (date: Date): string => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

// Equipment types enum
const EquipmentType = {
  GRINDER: 'grinder',
  ESPRESSO_MACHINE: 'espresso_machine',
  POUR_OVER: 'pour_over',
  FRENCH_PRESS: 'french_press',
  AEROPRESS: 'aeropress',
  MOKA_POT: 'moka_pot',
  SIPHON: 'siphon',
  TURKISH_POT: 'turkish_pot',
  SCALE: 'scale',
  KETTLE: 'kettle',
  FILTER: 'filter',
  THERMOMETER: 'thermometer',
  TIMER: 'timer',
  OTHER: 'other',
} as const;

// Price range enum
const PriceRange = {
  BUDGET: 'budget',
  MID_RANGE: 'mid_range',
  PREMIUM: 'premium',
  LUXURY: 'luxury',
} as const;

// Validation schema
const equipmentSchema = z.object({
  name: z.string().min(1, 'Name is required').max(200, 'Name too long'),
  brand: z.string().min(1, 'Brand is required').max(100, 'Brand too long'),
  model: z.string().max(100, 'Model too long').optional(),
  description: z
    .string()
    .min(10, 'Description must be at least 10 characters')
    .max(2000),
  equipment_type: z.enum([
    EquipmentType.GRINDER,
    EquipmentType.ESPRESSO_MACHINE,
    EquipmentType.POUR_OVER,
    EquipmentType.FRENCH_PRESS,
    EquipmentType.AEROPRESS,
    EquipmentType.MOKA_POT,
    EquipmentType.SIPHON,
    EquipmentType.TURKISH_POT,
    EquipmentType.SCALE,
    EquipmentType.KETTLE,
    EquipmentType.FILTER,
    EquipmentType.THERMOMETER,
    EquipmentType.TIMER,
    EquipmentType.OTHER,
  ]),
  // category_id will be auto-assigned by backend based on equipment_type
  category_id: z.string().optional(),
  price_range: z.enum([
    PriceRange.BUDGET,
    PriceRange.MID_RANGE,
    PriceRange.PREMIUM,
    PriceRange.LUXURY,
  ]),
  price_min: z.number().min(0).optional(),
  price_max: z.number().min(0).optional(),
  currency: z.string().default('USD'),
  affiliate_links: z.record(z.string(), z.string()).optional(),
  specifications: z.record(z.string(), z.any()).optional(),
  dimensions: z
    .object({
      width: z.number().optional(),
      height: z.number().optional(),
      depth: z.number().optional(),
      unit: z.string().default('cm'),
    })
    .optional(),
  weight: z.number().optional(),
  weight_unit: z.string().default('g'),
  materials: z.array(z.string()).optional(),
  color_options: z.array(z.string()).optional(),
  features: z.array(z.string()).optional(),
  featured_image_url: z.string().optional(),
  gallery_images: z
    .array(z.string())
    .max(10, 'Maximum 10 images allowed')
    .optional(),
  video_url: z.string().optional(),
  tags: z.array(z.string()).max(15, 'Maximum 15 tags allowed').optional(),
  // Admin-only fields - not exposed in user form, set by backend
  // is_featured: z.boolean().default(false),
  // is_verified: z.boolean().default(false),
  is_active: z.boolean().default(true),
});

type EquipmentFormData = z.infer<typeof equipmentSchema>;

interface RouteParams {
  equipmentId?: string;
}

// Tab button component with Apple/Nothing-inspired design
interface TabButtonProps {
  icon: React.ComponentType<any>;
  label: string;
  isActive: boolean;
  onPress: () => void;
  isDark: boolean;
  colors: any;
}

const TabButton: React.FC<TabButtonProps> = ({
  icon: Icon,
  label,
  isActive,
  onPress,
  isDark,
  colors,
}) => {
  const scaleAnim = useRef(new Animated.Value(1)).current;

  const handlePressIn = () => {
    Animated.spring(scaleAnim, {
      toValue: 0.95,
      useNativeDriver: true,
      friction: 8,
    }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scaleAnim, {
      toValue: 1,
      useNativeDriver: true,
      friction: 8,
    }).start();
  };

  return (
    <Pressable
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
    >
      <Animated.View
        style={[
          styles.tabButton,
          {
            transform: [{ scale: scaleAnim }],
            backgroundColor: isActive
              ? isDark
                ? 'rgba(255, 255, 255, 0.12)'
                : 'rgba(0, 0, 0, 0.06)'
              : 'transparent',
            borderColor: isActive
              ? isDark
                ? 'rgba(255, 255, 255, 0.2)'
                : 'rgba(0, 0, 0, 0.08)'
              : 'transparent',
          },
        ]}
      >
        <Icon
          size={18}
          color={isActive ? colors.primary : colors.onSurfaceVariant}
          strokeWidth={isActive ? 2.5 : 2}
        />
        <Text
          variant='labelMedium'
          style={[
            styles.tabLabel,
            {
              color: isActive ? colors.onSurface : colors.onSurfaceVariant,
              fontWeight: isActive ? '600' : '500',
            },
          ]}
        >
          {label}
        </Text>
      </Animated.View>
    </Pressable>
  );
};

// Action button with premium feel
interface ActionButtonProps {
  icon: React.ComponentType<any>;
  label: string;
  onPress: () => void;
  variant: 'primary' | 'secondary' | 'tertiary';
  disabled?: boolean;
  loading?: boolean;
  colors: any;
  isDark: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  icon: Icon,
  label,
  onPress,
  variant,
  disabled,
  loading,
  colors,
  isDark,
}) => {
  const scaleAnim = useRef(new Animated.Value(1)).current;

  const handlePressIn = () => {
    if (!disabled && !loading) {
      Animated.spring(scaleAnim, {
        toValue: 0.96,
        useNativeDriver: true,
        friction: 8,
      }).start();
    }
  };

  const handlePressOut = () => {
    Animated.spring(scaleAnim, {
      toValue: 1,
      useNativeDriver: true,
      friction: 8,
    }).start();
  };

  const getStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: colors.primary,
          textColor: colors.onPrimary,
          borderColor: 'transparent',
        };
      case 'secondary':
        return {
          backgroundColor: isDark
            ? 'rgba(255, 255, 255, 0.08)'
            : 'rgba(0, 0, 0, 0.04)',
          textColor: colors.onSurface,
          borderColor: isDark
            ? 'rgba(255, 255, 255, 0.15)'
            : 'rgba(0, 0, 0, 0.08)',
        };
      case 'tertiary':
        return {
          backgroundColor: 'transparent',
          textColor: colors.primary,
          borderColor: colors.primary,
        };
      default:
        return {
          backgroundColor: colors.surface,
          textColor: colors.onSurface,
          borderColor: colors.outline,
        };
    }
  };

  const buttonStyles = getStyles();

  return (
    <Pressable
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      disabled={disabled || loading}
    >
      <Animated.View
        style={[
          styles.actionButton,
          {
            transform: [{ scale: scaleAnim }],
            backgroundColor: buttonStyles.backgroundColor,
            borderColor: buttonStyles.borderColor,
            opacity: disabled ? 0.5 : 1,
          },
        ]}
      >
        <Icon size={16} color={buttonStyles.textColor} strokeWidth={2} />
        <Text
          variant='labelMedium'
          style={[styles.actionButtonLabel, { color: buttonStyles.textColor }]}
        >
          {label}
        </Text>
      </Animated.View>
    </Pressable>
  );
};

export function EquipmentCreateScreen() {
  const theme = useTheme();
  const colors = theme.colors;
  const isDark = theme.dark;
  const navigation = useNavigation();
  const route = useRoute();
  const { equipmentId } = (route.params as RouteParams) || {};

  const [activeTab, setActiveTab] = useState('basic');
  const [showPreview, setShowPreview] = useState(false);
  const [, setIsDraft] = useState(true);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(20)).current;

  // Ref to track latest form data for debounced saving
  const formDataRef = useRef<EquipmentFormData | undefined>(undefined);

  const createEquipment = useCreateEquipment();
  const updateEquipment = useUpdateEquipment();

  // Entry animation
  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  // Initialize form with default values
  const defaultValues: EquipmentFormData = {
    name: '',
    brand: '',
    model: '',
    description: '',
    equipment_type: EquipmentType.OTHER,
    category_id: undefined, // Will be auto-assigned by backend
    price_range: PriceRange.MID_RANGE,
    price_min: undefined,
    price_max: undefined,
    currency: 'USD',
    affiliate_links: {},
    specifications: {},
    dimensions: {
      width: undefined,
      height: undefined,
      depth: undefined,
      unit: 'cm',
    },
    weight: undefined,
    weight_unit: 'g',
    materials: [],
    color_options: [],
    features: [],
    featured_image_url: '',
    gallery_images: [],
    video_url: '',
    tags: [],
    // Admin-only fields removed - backend will set these
    is_active: true,
  };

  const methods = useForm<EquipmentFormData>({
    resolver: zodResolver(equipmentSchema),
    defaultValues,
  });

  const {
    watch,
    handleSubmit,
    formState: { errors, isDirty },
  } = methods;
  const formData = watch();

  // Draft saving functionality
  const draftKey = equipmentId
    ? `equipment-draft-${equipmentId}`
    : 'equipment-draft-new';
  const { saveDraft, loadDraft, clearDraft } = useDraftSaving(draftKey);

  // Update ref with latest form data
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    if (!isDirty) return;

    const interval = setInterval(() => {
      if (formDataRef.current) {
        saveDraft(formDataRef.current);
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [isDirty, saveDraft]);

  // Save draft on form data change (debounced)
  useEffect(() => {
    if (!isDirty) return;

    const timeoutId = setTimeout(() => {
      if (formDataRef.current) {
        saveDraft(formDataRef.current);
        setLastSavedAt(new Date());
      }
    }, 2000); // 2 seconds after last change

    return () => clearTimeout(timeoutId);
  }, [isDirty, saveDraft]);

  // Load draft on mount if creating new equipment
  useEffect(() => {
    if (!equipmentId) {
      const loadDraftData = async () => {
        const draft = await loadDraft();
        if (draft) {
          methods.reset(draft);
          setIsDraft(true);
        }
      };
      loadDraftData();
    }
  }, [equipmentId, loadDraft, methods]);

  const onSubmit = useCallback(
    async (data: EquipmentFormData, isPublish: boolean = false) => {
      try {
        const equipmentData = {
          ...data,
          is_active: isPublish,
        };

        if (equipmentId) {
          await updateEquipment.mutateAsync({
            id: equipmentId,
            equipment: equipmentData,
          });
          Toast.show('Equipment updated successfully!', 'success');
        } else {
          const newEquipment = await createEquipment.mutateAsync(equipmentData);
          clearDraft();
          Toast.show('Equipment created successfully!', 'success');
          (navigation as NavigationProp<any>).navigate('equipment', {
            screen: 'equipmentDetail',
            params: { equipmentId: newEquipment.id },
          });
        }
      } catch (error) {
        logger.error('Error saving equipment:', error);
        Toast.show('Failed to save equipment. Please try again.', 'error');
      }
    },
    [equipmentId, updateEquipment, createEquipment, clearDraft, navigation]
  );

  const handleSaveDraft = () => {
    handleSubmit(data => onSubmit(data, false))();
  };

  const handlePublish = () => {
    handleSubmit(data => onSubmit(data, true))();
  };

  const handleDiscard = useCallback(() => {
    CrossPlatformAlert.alert(
      'Discard Changes',
      'Are you sure you want to discard all unsaved changes? This cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Discard',
          style: 'destructive',
          onPress: () => {
            methods.reset(defaultValues);
            clearDraft();
            Toast.show('Changes discarded', 'info');
          },
        },
      ]
    );
  }, [methods, clearDraft]);

  const tabs = [
    { value: 'basic', label: 'Details', icon: FileText },
    { value: 'pricing', label: 'Pricing', icon: DollarSign },
    { value: 'specs', label: 'Specs', icon: Settings2 },
    { value: 'links', label: 'Links', icon: Link2 },
    { value: 'images', label: 'Media', icon: Image },
  ];

  const isLoading = createEquipment.isPending || updateEquipment.isPending;

  return (
    <FormProvider {...methods}>
      <SafeAreaView
        style={[styles.container, { backgroundColor: colors.background }]}
      >
        <KeyboardAvoidingView
          style={styles.keyboardAvoid}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <Animated.View
            style={[
              styles.animatedContainer,
              {
                opacity: fadeAnim,
                transform: [{ translateY: slideAnim }],
              },
            ]}
          >
            {/* Header - Apple-inspired minimalist design */}
            <View
              style={[
                styles.header,
                {
                  backgroundColor: isDark
                    ? 'rgba(28, 27, 31, 0.95)'
                    : 'rgba(255, 255, 255, 0.95)',
                  borderBottomColor: isDark
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.06)',
                },
              ]}
            >
              {/* Title Row */}
              <View style={styles.headerTitleRow}>
                <View style={styles.headerTitleContainer}>
                  <Coffee
                    size={28}
                    color={colors.primary}
                    style={{ marginRight: 12 }}
                  />
                  <Text
                    variant='headlineSmall'
                    style={[styles.headerTitle, { color: colors.onSurface }]}
                  >
                    {equipmentId ? 'Edit Equipment' : 'New Equipment'}
                  </Text>
                </View>
              </View>

              {/* Actions Row */}
              <View style={styles.headerActionsRow}>
                <View style={styles.headerActions}>
                  <ActionButton
                    icon={Eye}
                    label='Preview'
                    onPress={() => setShowPreview(true)}
                    variant='secondary'
                    disabled={isLoading}
                    colors={colors}
                    isDark={isDark}
                  />
                  <ActionButton
                    icon={Trash2}
                    label='Discard'
                    onPress={handleDiscard}
                    variant='tertiary'
                    disabled={isLoading}
                    colors={colors}
                    isDark={isDark}
                  />
                  <ActionButton
                    icon={Send}
                    label='Publish'
                    onPress={handlePublish}
                    variant='primary'
                    disabled={isLoading}
                    loading={isLoading}
                    colors={colors}
                    isDark={isDark}
                  />
                </View>
              </View>

              {/* Status row */}
              <View style={styles.statusRow}>
                <View
                  style={[
                    styles.statusBadge,
                    {
                      backgroundColor: isDirty
                        ? isDark
                          ? 'rgba(255, 183, 77, 0.15)'
                          : 'rgba(255, 152, 0, 0.12)'
                        : isDark
                          ? 'rgba(129, 199, 132, 0.15)'
                          : 'rgba(76, 175, 80, 0.12)',
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.statusDot,
                      {
                        backgroundColor: isDirty ? '#FFB74D' : '#81C784',
                      },
                    ]}
                  />
                  <Text
                    variant='labelSmall'
                    style={[
                      styles.statusText,
                      {
                        color: isDirty
                          ? isDark
                            ? '#FFB74D'
                            : '#E65100'
                          : isDark
                            ? '#81C784'
                            : '#2E7D32',
                      },
                    ]}
                  >
                    {isDirty ? 'Editing' : 'Saved'}
                  </Text>
                </View>

                {/* Last saved timestamp */}
                {lastSavedAt && (
                  <Text
                    variant='labelSmall'
                    style={[
                      styles.lastSavedText,
                      { color: colors.onSurfaceVariant },
                    ]}
                  >
                    Last saved {getTimeAgo(lastSavedAt)}
                  </Text>
                )}
              </View>
            </View>

            {/* Tab Navigation */}
            <View
              style={[
                styles.tabContainer,
                {
                  backgroundColor: isDark
                    ? 'rgba(28, 27, 31, 0.6)'
                    : 'rgba(255, 255, 255, 0.8)',
                  borderBottomColor: isDark
                    ? 'rgba(255, 255, 255, 0.06)'
                    : 'rgba(0, 0, 0, 0.04)',
                },
              ]}
            >
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.tabScrollContent}
              >
                {tabs.map(tab => (
                  <TabButton
                    key={tab.value}
                    icon={tab.icon}
                    label={tab.label}
                    isActive={activeTab === tab.value}
                    onPress={() => setActiveTab(tab.value)}
                    isDark={isDark}
                    colors={colors}
                  />
                ))}
              </ScrollView>
            </View>

            {/* Content Area */}
            <ScrollView
              style={styles.content}
              contentContainerStyle={styles.contentContainer}
              showsVerticalScrollIndicator={false}
            >
              <View style={styles.formSection}>
                {activeTab === 'basic' && <EquipmentBasicInfo />}
                {activeTab === 'pricing' && <EquipmentPricing />}
                {activeTab === 'specs' && <EquipmentSpecifications />}
                {activeTab === 'links' && <EquipmentAffiliateLinks />}
                {activeTab === 'images' && <EquipmentImages />}
              </View>

              {/* Form Errors */}
              {Object.keys(errors).length > 0 && (
                <View
                  style={[
                    styles.errorCard,
                    {
                      backgroundColor: isDark
                        ? 'rgba(244, 67, 54, 0.12)'
                        : 'rgba(244, 67, 54, 0.08)',
                      borderColor: isDark
                        ? 'rgba(244, 67, 54, 0.3)'
                        : 'rgba(244, 67, 54, 0.2)',
                    },
                  ]}
                >
                  <View style={styles.errorHeader}>
                    <View
                      style={[
                        styles.errorIconContainer,
                        {
                          backgroundColor: isDark
                            ? 'rgba(244, 67, 54, 0.2)'
                            : 'rgba(244, 67, 54, 0.15)',
                        },
                      ]}
                    >
                      <X size={14} color='#F44336' strokeWidth={2.5} />
                    </View>
                    <Text
                      variant='titleSmall'
                      style={[
                        styles.errorTitle,
                        { color: isDark ? '#EF9A9A' : '#C62828' },
                      ]}
                    >
                      Please fix the following
                    </Text>
                  </View>
                  <View style={styles.errorList}>
                    {Object.entries(errors).map(([field, error]) => (
                      <Text
                        key={field}
                        variant='bodySmall'
                        style={[
                          styles.errorItem,
                          { color: isDark ? '#FFCDD2' : '#B71C1C' },
                        ]}
                      >
                        • {field.replace(/_/g, ' ')}: {error?.message}
                      </Text>
                    ))}
                  </View>
                </View>
              )}

              {/* Bottom spacing */}
              <View style={styles.bottomSpacer} />
            </ScrollView>
          </Animated.View>

          {/* Preview Modal */}
          <Portal>
            <Modal
              visible={showPreview}
              onDismiss={() => setShowPreview(false)}
              contentContainerStyle={[
                styles.previewModal,
                { backgroundColor: colors.surface },
              ]}
            >
              <View
                style={[
                  styles.previewHeader,
                  {
                    borderBottomColor: isDark
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(0, 0, 0, 0.06)',
                  },
                ]}
              >
                <View style={styles.previewHeaderContent}>
                  <Eye size={20} color={colors.primary} strokeWidth={2} />
                  <Text
                    variant='titleMedium'
                    style={[styles.previewTitle, { color: colors.onSurface }]}
                  >
                    Equipment Preview
                  </Text>
                </View>
                <Pressable
                  onPress={() => setShowPreview(false)}
                  style={[
                    styles.closeButton,
                    {
                      backgroundColor: isDark
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'rgba(0, 0, 0, 0.04)',
                    },
                  ]}
                >
                  <X
                    size={18}
                    color={colors.onSurfaceVariant}
                    strokeWidth={2}
                  />
                </Pressable>
              </View>
              <ScrollView
                style={styles.previewContent}
                contentContainerStyle={styles.previewContentContainer}
              >
                <EquipmentPreview data={formData} />
              </ScrollView>
            </Modal>
          </Portal>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </FormProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardAvoid: {
    flex: 1,
  },
  animatedContainer: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 16,
    borderBottomWidth: 1,
  },
  headerTitleRow: {
    marginBottom: 12,
  },
  headerTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitle: {
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  headerActionsRow: {
    marginBottom: 12,
  },
  headerActions: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 12,
    borderWidth: 1,
  },
  actionButtonLabel: {
    fontWeight: '600',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  statusText: {
    fontWeight: '600',
    fontSize: 11,
  },
  lastSavedText: {
    fontSize: 11,
  },
  tabContainer: {
    borderBottomWidth: 1,
    paddingVertical: 8,
  },
  tabScrollContent: {
    paddingHorizontal: 16,
    gap: 8,
  },
  tabButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 12,
    borderWidth: 1,
  },
  tabLabel: {
    fontSize: 13,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
  },
  formSection: {
    marginBottom: 20,
  },
  errorCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 16,
    marginBottom: 20,
  },
  errorHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
  },
  errorIconContainer: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorTitle: {
    fontWeight: '600',
  },
  errorList: {
    gap: 6,
  },
  errorItem: {
    fontSize: 12,
    lineHeight: 18,
  },
  bottomSpacer: {
    height: 40,
  },
  previewModal: {
    margin: 20,
    maxHeight: '90%',
    borderRadius: 20,
    overflow: 'hidden',
  },
  previewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  previewHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  previewTitle: {
    fontWeight: '600',
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  previewContent: {
    flex: 1,
  },
  previewContentContainer: {
    paddingVertical: 20,
  },
});
